import {
  CALL_ACTION_COPY,
  VIDEO_INVITE_COPY,
  WARM_TRANSFER_COPY,
} from 'Modules/softphone/constants/callActionCopy';
import type {
  SoftphoneActionBlockers,
  SoftphoneCallActionCopyOverride,
  SoftphoneCallActionState,
  SoftphoneCallActionWording,
  SoftphoneShortcutAction,
} from 'Modules/softphone/types';

/**
 * The caption and tooltip an action should show in the given state.
 *
 * Disabled buttons resolve to the reason (or the generic `unavailable` copy);
 * available ones to the tooltip, falling back to the caption when the action
 * needs no richer wording.
 */
export const getCallActionCopy = (
  action: SoftphoneShortcutAction,
  {
    isActive = false,
    isDisabled = false,
    reason,
    override,
  }: SoftphoneCallActionState = {}
): SoftphoneCallActionWording => {
  const copy = CALL_ACTION_COPY[action];

  // The action's wording for this state, with the context's take layered on top
  // part by part — an override states only what it changes.
  const state = isActive && (copy.active || override?.active) ? 'active' : 'default';
  const wording = copy[state] ?? copy.default;
  const caption = override?.[state]?.caption ?? wording.caption;
  const tooltip = override?.[state]?.tooltip ?? wording.tooltip;
  const unavailable = override?.unavailable ?? copy.unavailable;

  if (isDisabled) {
    // An action that is disabled *while toggled on* is mid-flight (Merging...),
    // so it describes what it's doing rather than the precondition it needs.
    // `||`, not `??`: an empty reason is no reason.
    return { caption, tooltip: reason || (isActive ? caption : unavailable) };
  }

  // An action with no richer wording just repeats its caption on hover.
  return { caption, tooltip: tooltip ?? caption };
};

/**
 * Whether an action renames itself when toggled on (Mute becomes Unmute). Such
 * an action must not also carry `aria-pressed`. A property of the copy, not of
 * the state, so a button does not change kind mid-call.
 */
export const hasStateVaryingCaption = (
  action: SoftphoneShortcutAction,
  override?: SoftphoneCallActionCopyOverride
): boolean =>
  Boolean(
    override?.active?.caption ?? CALL_ACTION_COPY[action]?.active?.caption
  );

/**
 * The copy an incoming call's Accept/Decline shows, worded for the kind of call
 * being offered — a video invite is joined, not "accepted".
 *
 * Same split as {@link getUnavailableReason}: the caller knows what kind of
 * call it is, this knows which words that picks.
 */
export const getCallInviteCopy = (
  action: SoftphoneShortcutAction,
  { isVideo = false }: { isVideo?: boolean } = {}
): SoftphoneCallActionWording =>
  getCallActionCopy(action, {
    override: isVideo ? VIDEO_INVITE_COPY[action] : undefined,
  });

/**
 * Why an action is off-limits right now, when the cause is more specific than
 * its generic `unavailable` copy — feed the result to `getCallActionCopy` as
 * `reason` (or to a button as `unavailableReason`).
 *
 * Undefined means nothing more specific applies, so the action keeps its
 * authored wording. Kept beside the copy it returns, and given the flags rather
 * than reading them, so it stays a pure lookup like the rest of this module —
 * the caller owns the store, this owns the words and their precedence.
 */
export const getUnavailableReason = (
  action: SoftphoneShortcutAction,
  { isWarmTransferConsultationAnswered, isMerging }: SoftphoneActionBlockers
): string | undefined => {
  // A merge in flight already describes what it's doing ("Merging..."), so it
  // outranks any precondition.
  if (isMerging) {
    return undefined;
  }

  // An action with no entry isn't blocked by the consultation at all.
  return isWarmTransferConsultationAnswered
    ? WARM_TRANSFER_COPY.blocked[action]?.unavailable
    : undefined;
};
